import { Heading } from "@/components/base/heading";
import { InputGroup } from "@/components/base/input/input-group";
import { SearchAndSelect } from "@/components/base/input/search-and-select";
import { Select } from "@/components/base/input/select";
import { HorizontalContainer } from "@/components/layout/horizontal-container";
import { HorizontalSpacer } from "@/components/layout/horizontal-spacer";
import { PageContent } from "@/components/layout/page-content";
import { VerticalContainer } from "@/components/layout/vertical-container";
import { VisuallyHidden } from "@/components/layout/visually-hidden";
import { LinkButton } from "@/components/ui/link-button";
import { Text } from "@/components/ui/text";
import { useBliMedlemCentersSuspenseQuery, useOrganizationsSuspenseQuery } from "@/generated/client.generated";
import { paths } from "@/utils/paths-utils";
import { parseAsString, useQueryState } from "nuqs";
import { type ChangeEvent, useContext, useState } from "react";
import { sendStepEvent } from "../analytics";
import { FormStateContext } from "../form-state-context";
import { UserJourney } from "../types";
import { sortCenters } from "../utils";

export const OrganizationWrapper = () => {
  const { formData, currentStepIndex, step, next, setFormData } = useContext(FormStateContext);

  const { data: centerData } = useBliMedlemCentersSuspenseQuery();
  const { data: organizationsData } = useOrganizationsSuspenseQuery();

  const [organizationQueryParam] = useQueryState("organization", parseAsString.withDefault(""));
  const [mainCenterIdQueryParam] = useQueryState("mainCenterId", parseAsString.withDefault(""));

  const [organization, setOrganization] = useState<string>(organizationQueryParam);
  const [mainCenterId, setMainCenterId] = useState<string>(mainCenterIdQueryParam);

  if (!(centerData && organizationsData)) {
    return <></>;
  }

  const sortedCenters = sortCenters([...centerData.Centers]);
  const organizations = organizationsData?.Organizations.map((organization) => {
    return { label: organization.name, value: organization.id };
  });
  return (
    <PageContent width="small">
      <VisuallyHidden>
        <Heading type="h2">Skriv inn personalia i skjemaet</Heading>
      </VisuallyHidden>
      <InputGroup>
        <HorizontalContainer horizontalPlacement="left">
          <Heading type="h3">Finn din bedrift!</Heading>
        </HorizontalContainer>
        <HorizontalSpacer />
        <SearchAndSelect
          searchSource={organizations}
          selected={organizations?.find((o) => o.value === organization)?.value ?? ""}
          setSelected={(value: string) => {
            setOrganization(value);
            sendStepEvent(value, currentStepIndex + 1, step?.shortTitle, formData.userJourney);
            setFormData({
              ...formData,
              organization: organizationsData?.Organizations.find((o) => o.id === value),
            });
          }}
          placeholder="Søk etter din bedrift"
          id="organization"
          name="organization"
          noHitsErrorMessage="Vi fant dessverre ingen bedrift som matcher ditt søk."
        />
      </InputGroup>

      <HorizontalSpacer size="large" />

      <InputGroup>
        <HorizontalContainer horizontalPlacement="left">
          <Heading type="h3">Hvor ønsker du trene mest?</Heading>
        </HorizontalContainer>

        <HorizontalSpacer />
        <Select
          options={sortedCenters.map((center) => center.page.title)}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            const center = sortedCenters.find((center) => center.page.title === e.target.value);
            setMainCenterId(center?.id ?? "");
          }}
          placeholder="Velg ditt favorittsenter"
          value={sortedCenters.find((center) => center.id === mainCenterId)?.page.title ?? ""}
          id={"mainCenter"}
          name={"mainCenter"}
          fullWidth={true}
        />
      </InputGroup>

      <HorizontalSpacer size="large" />

      <VerticalContainer horizontalPlacement="center">
        <Text value="Finn din bedrift og velg ditt favorittsenter for å gå videre" align="center" />
        <HorizontalSpacer />
        <LinkButton
          buttonType="primary"
          onClick={() => next()}
          disabled={organization === "" || mainCenterId === ""}
          href={paths.bliMedlemPrefilled(
            UserJourney.Organization,
            formData.customerType?.id,
            mainCenterId ?? undefined,
            formData.accountType?.id,
            formData.accountType?.discountCode?.code,
            formData.contactInfo.email,
            undefined,
            organization ?? undefined,
          )}
        >
          <Text value="Neste" />
        </LinkButton>
      </VerticalContainer>
    </PageContent>
  );
};
